import { useState } from 'react'
import { FormikProvider, useFormik } from 'formik'

import { Alert, Dialog, Button, Form, Text } from '@/ui'
import { adminUpdateUserWithId } from '@/actions/admin/adminAllUsersActions'
import { UserWithAdminInfo } from '@/reducers/admin/allUsersReducer'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { selectIsAccountingOpsManager } from '@/selectors/user.selectors'
import { TypographyStyles } from '@/styles'
import { getFieldNames, makeReqStringSchema } from '@/base'

interface UnlockBooksModalProps {
  open: boolean
  close: () => void
  user: Pick<
    UserWithAdminInfo,
    | 'id'
    | 'firstName'
    | 'lastName'
    | 'booksLockedForUserYear'
    | 'booksLockedForAdminYear'
  >
}

const NOT_LOCKED = 'not_locked'
const currentYear = new Date().getFullYear()
const yearOptions = [
  { text: 'Not locked', value: NOT_LOCKED },
  ...Array.from({ length: currentYear - 2019 }, (_, i) => {
    const year = String(currentYear - i)
    return { text: year, value: year }
  }),
]

export const UnlockBooksModal = ({
  open,
  close,
  user,
}: UnlockBooksModalProps) => {
  const dispatch = useAppDispatch()
  const isAccountingOpsManager = useReselector(selectIsAccountingOpsManager)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const filterYearOptions = (currentLockedYear: string | null) => {
    if (isAccountingOpsManager) return yearOptions
    if (!currentLockedYear) return yearOptions
    return yearOptions.filter(
      (opt) => opt.value !== NOT_LOCKED && opt.value >= currentLockedYear
    )
  }

  const userYearOptions = filterYearOptions(user.booksLockedForUserYear)
  const adminYearOptions = filterYearOptions(user.booksLockedForAdminYear)

  const formik = useFormik({
    initialValues: {
      booksLockedForUserYear: user.booksLockedForUserYear ?? NOT_LOCKED,
      booksLockedForAdminYear: user.booksLockedForAdminYear ?? NOT_LOCKED,
      bookLockReason: '',
    },
    onSubmit: () => {
      setShowConfirmation(true)
    },
  })

  const { dirty, isSubmitting, submitForm, values } = formik
  const fieldNames = getFieldNames(formik)

  const handleConfirm = async () => {
    setSubmitting(true)

    const payload: Record<string, string | null> = {
      bookLockReason: values.bookLockReason,
    }

    if (
      values.booksLockedForUserYear !==
      (user.booksLockedForUserYear ?? NOT_LOCKED)
    ) {
      payload.booksLockedForUserYear =
        values.booksLockedForUserYear === NOT_LOCKED
          ? null
          : values.booksLockedForUserYear
    }
    if (
      values.booksLockedForAdminYear !==
      (user.booksLockedForAdminYear ?? NOT_LOCKED)
    ) {
      payload.booksLockedForAdminYear =
        values.booksLockedForAdminYear === NOT_LOCKED
          ? null
          : values.booksLockedForAdminYear
    }

    const result = await adminUpdateUserWithId(user.id, payload)(dispatch)
    if (result.error) {
      setShowConfirmation(false)
    } else {
      close()
    }
    setSubmitting(false)
  }

  const handleClose = () => {
    setShowConfirmation(false)
    formik.resetForm()
    close()
  }

  if (showConfirmation) {
    return (
      <Dialog open={open} onClose={handleClose}>
        <Dialog.Content size="md">
          <Text variant={TypographyStyles.H1}>Confirm Book Lock Changes</Text>
          <Text variant={TypographyStyles.BODY_MD} className="mt-2">
            Please review the changes below for {user.firstName} {user.lastName}
            :
          </Text>
          <div className="mt-4 space-y-3 rounded border p-4">
            <div className="flex justify-between">
              <Text variant={TypographyStyles.BODY_SM}>User Year Lock</Text>
              <Text variant={TypographyStyles.BODY_SM}>
                {user.booksLockedForUserYear ?? 'Not locked'} &rarr;{' '}
                <strong>
                  {values.booksLockedForUserYear === NOT_LOCKED
                    ? 'Not locked'
                    : values.booksLockedForUserYear}
                </strong>
              </Text>
            </div>
            <div className="flex justify-between">
              <Text variant={TypographyStyles.BODY_SM}>Admin Year Lock</Text>
              <Text variant={TypographyStyles.BODY_SM}>
                {user.booksLockedForAdminYear ?? 'Not locked'} &rarr;{' '}
                <strong>
                  {values.booksLockedForAdminYear === NOT_LOCKED
                    ? 'Not locked'
                    : values.booksLockedForAdminYear}
                </strong>
              </Text>
            </div>
            <div className="border-t pt-3">
              <Text
                variant={TypographyStyles.BODY_XS}
                className="text-gray-500"
              >
                Reason
              </Text>
              <Text variant={TypographyStyles.BODY_SM}>
                {values.bookLockReason}
              </Text>
            </div>
          </div>
          <Dialog.Footer className="mt-4">
            <Button
              variant="secondary"
              onClick={() => setShowConfirmation(false)}
              disabled={submitting}
            >
              Back
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={submitting}
            >
              {submitting ? 'Updating...' : 'Confirm'}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onClose={handleClose}>
      <Dialog.Content size="md">
        <Text variant={TypographyStyles.H1}>Manage Locked Book Year</Text>
        {!isAccountingOpsManager && (
          <Alert
            variant="warning"
            className="mt-3"
            description="Only Accounting Ops Managers can reopen locked books."
          />
        )}
        <FormikProvider value={formik}>
          <div className="mt-4 space-y-4">
            <Form.Select
              name={fieldNames.booksLockedForUserYear}
              label="User Year Lock"
              options={userYearOptions}
              placeholder="Select year"
              description={`Currently: ${user.booksLockedForUserYear ?? 'Not locked'}`}
              schema={makeReqStringSchema()}
            />
            <Form.Select
              name={fieldNames.booksLockedForAdminYear}
              label="Admin Year Lock"
              options={adminYearOptions}
              placeholder="Select year"
              description={`Currently: ${user.booksLockedForAdminYear ?? 'Not locked'}`}
              schema={makeReqStringSchema()}
            />
            <Form.Textarea
              name={fieldNames.bookLockReason}
              label="Reason"
              placeholder="e.g., TY24 amendment per ZD #219292"
              rows={3}
              schema={makeReqStringSchema()}
            />
          </div>
          <Dialog.Footer className="mt-4">
            <Dialog.Close asChild>
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              variant="primary"
              onClick={submitForm}
              disabled={!dirty || isSubmitting}
            >
              Review Changes
            </Button>
          </Dialog.Footer>
        </FormikProvider>
      </Dialog.Content>
    </Dialog>
  )
}
